import { FC } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  CheckboxInput,
  FormContainer,
  NextButton,
  Option,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  StringInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { requiredArrayOfStrings } from "~/utils/validations";

type Values = {
  supportNeeds: string[];
  supportNeedsOtherDetails: string;
};

const validationSchema = Yup.object({
  supportNeeds: requiredArrayOfStrings,
  supportNeedsOtherDetails: Yup.string().test(
    "supportNeedsOtherDetails",
    'This field is required when "Other" is selected.',
    function (value) {
      const { supportNeeds } = this.parent;
      return supportNeeds && supportNeeds.includes("Other")
        ? Boolean(value)
        : true;
    },
  ),
});

const options: Option[] = [
  {
    text: "A community of peers in the same journey",
    value: "community_peers",
  },
  { text: "Text support to avoid Google rabbit holes", value: "text_support" },
  {
    text: "1:1 support from those who have been in your shoes",
    value: "one_on_one_support",
  },
  { text: "Calls with nurses", value: "nurse_calls" },
  { text: "Live injection support", value: "injection_support" },
  { text: "Other", value: "Other" },
];

export const SupportNeedsPage: FC = () => {
  const { supportNeeds, supportNeedsOtherDetails } = useOnboarding();
  const { onSubmit } = useSubmitOnboardingQuestion();

  const initialValues: Values = {
    supportNeeds: supportNeeds || [],
    supportNeedsOtherDetails: supportNeedsOtherDetails || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion =
    formik?.values?.supportNeeds?.includes("Other");

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            question="What do you need the most right now?"
            description="Choose all that apply"
          />

          <CheckboxInput
            defaultValue={initialValues.supportNeeds}
            id="supportNeeds"
            options={options}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer my={5}>
            <Prompt question="What other support do you need?" />

            <StringInput
              id="supportNeedsOtherDetails"
              placeholder="Please specify"
            />
          </QuestionContainer>
        )}

        <NextButton />
      </FormContainer>
    </QuestionPageContainer>
  );
};
